/**
 * Offer Cart Display (international only, v1)
 *
 * Customer-facing cart reflection of the Offer Engine. It reuses the SAME engine that
 * builds the Stripe-worker payload (assets/offer-engine.js), so the discount shown in the
 * cart is guaranteed to match the amount actually charged.
 *
 * What it does when an offer is active for the shopper's country:
 *   - Fetches the live cart (/cart.js).
 *   - Converts each line's unit price to the market currency the SAME way the checkout does
 *     (WebhookHelper.convertToMarketCurrency + roundUpPrice, i.e. INR -> market, rounded up).
 *     This exactly mirrors the server-side subtotal in snippets/cart-price.liquid.
 *   - Runs OfferEngine.apply() to get the total discount.
 *   - Injects an "Offer (label): -<amount>" row into the cart totals and rewrites the
 *     displayed subtotal to (base subtotal - discount).
 *
 * It never touches the real Shopify cart (theme cannot change real line prices anyway).
 * When no offer is active it does nothing and leaves the native totals untouched.
 *
 * Depends on: window.OfferEngine, and window.WebhookHelper OR window.MarketConfig.
 */
(function () {
  'use strict';

  var INJECTED_ROW_CLASS = 'offer-engine-cart-row';
  var OBSERVER_OPTS = { childList: true, subtree: true, characterData: true };
  var applying = false;
  var scheduled = false;
  var observer = null;

  // Our own DOM writes (rewriting the subtotal text, injecting the offer row) must NOT be
  // seen by the observer — otherwise each write schedules another run and we spin forever,
  // hammering /cart.js and fighting the theme's own cart re-renders (e.g. discount removal).
  // Disconnect around our writes and drop any records they queued before reconnecting.
  function withObserverPaused(fn) {
    if (observer) observer.disconnect();
    try { fn(); } finally {
      if (observer) { observer.takeRecords(); observer.observe(document.body, OBSERVER_OPTS); }
    }
  }

  function convertToMarketCurrency(priceInCents) {
    if (window.WebhookHelper && typeof window.WebhookHelper.convertToMarketCurrency === 'function') {
      return window.WebhookHelper.convertToMarketCurrency(priceInCents);
    }
    if (window.MarketConfig && typeof window.MarketConfig.convertPrice === 'function') {
      return window.MarketConfig.convertPrice(priceInCents);
    }
    return null; // no converter available -> bail out (do not guess)
  }

  function roundUpPrice(price) {
    if (window.WebhookHelper && typeof window.WebhookHelper.roundUpPrice === 'function') {
      return window.WebhookHelper.roundUpPrice(price);
    }
    return Math.ceil(price);
  }

  // Full display string for a market amount, in the current locale's own
  // convention ("AED 1,234.00" / "$1,234.00" / "1.234,00 €" / "€ 1.234,00").
  // Delegates to the shared formatter so the rewritten subtotal matches the
  // theme's own rendering; `prefix` is only the fallback when it is absent.
  function money(amount, prefix) {
    if (window.MarketConfig && typeof window.MarketConfig.formatDisplay === 'function') {
      return window.MarketConfig.formatDisplay(Number(amount) || 0);
    }
    var n = Number(amount);
    if (!isFinite(n)) n = 0;
    var parts = n.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (prefix || '') + parts[0] + '.' + parts[1];
  }

  // Fallback only: the leading non-digit run of the displayed subtotal (e.g.
  // "$", "AED ", "₹") when MarketConfig.formatDisplay is unavailable.
  function currencyPrefixFrom(text) {
    if (!text) return '';
    var m = String(text).match(/^[^\d]*/);
    return m ? m[0] : '';
  }

  function isMtoProps(props) {
    if (!props) return false;
    // Only the plating service FEE line is excluded from offers; a product
    // carrying the '18kt Gold plating: Yes' display property is a normal,
    // offer-eligible product (the fee line carries _Service).
    return !!props._Service;
  }

  // Build engine items from a /cart.js payload, mirroring the checkout's price basis.
  function buildOfferItems(cart) {
    if (!cart || !cart.items) return null;
    var items = [];
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var converted = convertToMarketCurrency(it.final_price);
      if (converted === null) return null; // converter missing -> abort quietly
      items.push({
        key: String(it.variant_id) + ':' + i,
        quantity: it.quantity,
        unitPrice: roundUpPrice(converted),
        isMto: isMtoProps(it.properties)
      });
    }
    return items;
  }

  function baseSubtotalUnits(offerItems) {
    var t = 0;
    for (var i = 0; i < offerItems.length; i++) {
      t += offerItems[i].unitPrice * offerItems[i].quantity;
    }
    return t;
  }

  // Remove any rows/edits we previously injected, so each pass starts clean.
  function cleanup(root) {
    var rows = root.querySelectorAll('.' + INJECTED_ROW_CLASS);
    for (var i = 0; i < rows.length; i++) rows[i].parentNode.removeChild(rows[i]);
    var totals = root.querySelectorAll('[data-cart-total][data-offer-base]');
    for (var j = 0; j < totals.length; j++) {
      var el = totals[j];
      var base = el.getAttribute('data-offer-base-text');
      if (base !== null) el.textContent = base;
      el.removeAttribute('data-offer-base');
      el.removeAttribute('data-offer-base-text');
    }
  }

  function applyToContainer(root, result, offerItems) {
    var subtotalEl = root.querySelector('[data-cart-total]');
    if (!subtotalEl) return false;

    var baseUnits = baseSubtotalUnits(offerItems);
    var discount = result.totalDiscount;
    if (!(discount > 0) || baseUnits <= 0) return false;

    var currentText = subtotalEl.textContent.trim();
    var prefix = currencyPrefixFrom(currentText);
    var newUnits = Math.max(0, baseUnits - discount);

    // Stash the original text once so cleanup() can restore it before re-applying.
    if (!subtotalEl.hasAttribute('data-offer-base')) {
      subtotalEl.setAttribute('data-offer-base', '1');
      subtotalEl.setAttribute('data-offer-base-text', currentText);
    }
    // Slashed original + discounted amount, so the applied offer is obvious at
    // a glance. Built as DOM nodes (no innerHTML). cleanup() restores via
    // textContent, which clears these children. The original amount stays FIRST
    // in textContent so price-prefix parsing elsewhere still reads correctly.
    subtotalEl.textContent = '';
    var strike = document.createElement('s');
    strike.textContent = money(baseUnits, prefix);
    strike.style.opacity = '.55';
    strike.style.fontWeight = '400';
    strike.style.fontSize = '.85em';
    strike.style.marginRight = '8px';
    subtotalEl.appendChild(strike);
    subtotalEl.appendChild(document.createTextNode(money(newUnits, prefix)));

    // Build the discount row and insert it right before the subtotal's row.
    var subtotalRow = subtotalEl.closest('.cart__total') || subtotalEl.parentNode;
    var row = document.createElement('div');
    row.className = 'cart__total ' + INJECTED_ROW_CLASS;
    row.setAttribute('data-offer-label', result.offerLabel || 'Offer');

    var label = document.createElement('span');
    label.className = 'cart__total__label';
    // Localised row label: Arabic pages say "العرض", everything else "Offer".
    var isAr = String(document.documentElement.lang || '').toLowerCase().indexOf('ar') === 0;
    var offerWord = isAr ? 'العرض' : 'Offer';
    label.textContent = offerWord + (result.offerLabel ? ' (' + result.offerLabel + ')' : '');

    var amount = document.createElement('span');
    amount.className = 'cart__total__price';
    amount.textContent = '- ' + money(discount, prefix);

    row.appendChild(label);
    row.appendChild(amount);
    if (subtotalRow && subtotalRow.parentNode) {
      subtotalRow.parentNode.insertBefore(row, subtotalRow);
    }
    return true;
  }

  function run() {
    if (applying) return;
    if (!window.OfferEngine || typeof window.OfferEngine.apply !== 'function') return;

    // Cheap gate: only do work when an offer is actually active for this country.
    var active = (typeof window.OfferEngine.getActiveOffer === 'function')
      ? window.OfferEngine.getActiveOffer()
      : null;

    // Containers that can show cart totals (cart page + cart drawer).
    var containers = [];
    var pushSel = function (sel) {
      var nodes = document.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) containers.push(nodes[i]);
    };
    pushSel('cart-drawer');
    pushSel('.cart-drawer');
    pushSel('.cart__footer');
    pushSel('.template-cart');
    pushSel('#cart');
    // Fallback: any subtotal element on the page.
    if (!containers.length && document.querySelector('[data-cart-total]')) {
      containers.push(document.body);
    }
    if (!containers.length) return;

    // No active offer: just clean up anything we injected earlier and stop.
    if (!active) {
      withObserverPaused(function () {
        for (var c = 0; c < containers.length; c++) cleanup(containers[c]);
      });
      return;
    }

    applying = true;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var offerItems = buildOfferItems(cart);
        if (!offerItems || !offerItems.length) return;
        var result = window.OfferEngine.apply(offerItems);
        withObserverPaused(function () {
          for (var i = 0; i < containers.length; i++) {
            cleanup(containers[i]);
            if (result.applied) applyToContainer(containers[i], result, offerItems);
          }
        });
      })
      .catch(function () { /* leave native totals untouched on any error */ })
      .then(function () { applying = false; });
  }

  // Debounced scheduler so bursts of DOM mutations trigger a single recompute.
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () { scheduled = false; run(); }, 120);
  }

  function init() {
    schedule();

    // Re-apply when the cart re-renders (drawer open, line qty change, add-to-cart).
    // Our own writes are hidden from the observer via withObserverPaused(), so this only
    // fires on genuine cart changes from the theme.
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          // Ignore mutations within our own injected row.
          if (m.target && m.target.classList &&
              m.target.classList.contains(INJECTED_ROW_CLASS)) continue;
          schedule();
          return;
        }
      });
      observer.observe(document.body, OBSERVER_OPTS);
    }

    // Common theme cart events (best-effort; harmless if never fired).
    ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added'].forEach(function (ev) {
      document.addEventListener(ev, schedule);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
